import { call, put, select, takeLatest } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  scheduleCampaignRequest,
  scheduleCampaignSuccess,
  scheduleCampaignFailure,
  deleteScheduleRequest,
  deleteScheduleSuccess,
  deleteScheduleFailure,
  type ScheduleCampaignPayload,
  type DeleteSchedulePayload,
} from '../slices/campaignScheduleSlice';
import { fetchCampaignsRequest } from '../slices/campaignsSlice';
import ENDPOINTS from '../../api/endPoints';
import type { RootState } from '../store';

// Schedule Campaign Saga
function* scheduleCampaignSaga(action: PayloadAction<ScheduleCampaignPayload>) {
  try {
    const { campaignId, scheduleData } = action.payload;
    
    // Get restaurantId from store
    const restaurantId: string = yield select((state: RootState) => state.clover.restaurantId);
    
    if (!restaurantId) {
      throw new Error('Restaurant ID not found');
    }

    const response: { data: any } = yield call(
      axios.put,
      ENDPOINTS.scheduleCampaign(campaignId),
      scheduleData,
      {
        headers: {
          'restaurant-id': restaurantId,
        },
      }
    );

    yield put(scheduleCampaignSuccess(response.data));
    
    // Refresh campaigns list after scheduling
    yield put(fetchCampaignsRequest());
    
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to schedule campaign';
    yield put(scheduleCampaignFailure(errorMessage));
  }
}

// Delete Schedule Saga
function* deleteScheduleSaga(action: PayloadAction<DeleteSchedulePayload>) {
  try {
    const { campaignId } = action.payload;
    
    // Get restaurantId from store
    const restaurantId: string = yield select((state: RootState) => state.clover.restaurantId);
    
    if (!restaurantId) {
      throw new Error('Restaurant ID not found');
    }

    yield call(
      axios.delete,
      ENDPOINTS.deleteSchedule(campaignId),
      {
        headers: {
          'restaurant-id': restaurantId,
        },
      }
    );

    yield put(deleteScheduleSuccess());
    
    // Refresh campaigns list after deleting schedule
    yield put(fetchCampaignsRequest());
    
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to delete schedule';
    yield put(deleteScheduleFailure(errorMessage));
  }
}

// Root Saga
export default function* campaignScheduleSaga() {
  yield takeLatest(scheduleCampaignRequest.type, scheduleCampaignSaga);
  yield takeLatest(deleteScheduleRequest.type, deleteScheduleSaga);
}
